import styled from 'styled-components';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {setIn} from "immutable";
import {HtmlRenderer, Parser} from "commonmark";
import {useCreateComment} from '../api/comment';
import {themecolors} from '../utils/themecolors';
import useFeedback from "../utils/use_feedback";
import {useLoggedIn, useStorePost} from "../store";
import {useActions} from "../app_context";
import {useEditComment, useEditPost, useRawContent} from "../api/edit";
import renderMarkdown from "../utils/render_markdown";
import Button from './button';
import {useSheetModal} from "./test/ModalContext";
import Sheet from "./bottom-sheet/sheet";
import SignupPromptSheet from "./bottom-sheet/signup_prompt";

const Footer = styled.div``;
const Save = styled.div``;
const PostButton = styled(Button)``;
const CancelButton = styled(Button)``;
const TextAreaContainer = styled.div``;

const TextInputBox = styled(({className, actionLabel = "Post", placeholder = "Add a comment", onSubmit, unopened, hideCancel, initialContent, onClose}) => {

    const creating = useFeedback();
    const [content, setContent] = useState('');
    const [posting, setPosting] = useState(false);
    const [expanded, setExpanded] = useState(!unopened);

    const textareaRef = useRef(null);

    const adjustHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            const newHeight = textareaRef.current.scrollHeight;
            textareaRef.current.style.height = `${Math.min(newHeight, 250)}px`;
        }
    };

    useEffect(() => {
        if (initialContent !== undefined && content !== initialContent) {
            setContent(initialContent);
        }
    }, [initialContent]);

    const handleSubmit = async () => {
        if (posting || !content.trim()) return;
        try {
            setPosting(true);
            await onSubmit(content);
            setContent('');
            setExpanded(false);
            if (typeof onClose === 'function') onClose();
        } catch {
            // error handling is assumed to be done inside `onSubmit`
        }
        setPosting(false);
    };

    useEffect(() => {
        if (content.length > 0) {
            adjustHeight();
        }
    }, [content]);

    const loggedIn = useLoggedIn();
    const { openModal } = useSheetModal();
    const handleExpand = () => {

        if (!loggedIn) {
            const modalContent = ({ handleCloseModal }) => (
                <Sheet onClose={() => handleCloseModal() } padding={"header"}><SignupPromptSheet onClose={() => handleCloseModal() } /></Sheet>
            );

            openModal(modalContent);
        } else {
            setExpanded(true);
        }

    }

    return (
        <div className={className}>
            <TextAreaContainer onClick={handleExpand}>
                <textarea
                    ref={textareaRef}
                    id="content"
                    placeholder={placeholder}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    autoFocus={expanded}
                    disabled={!loggedIn}
                    style={{ 
                        pointerEvents: loggedIn ? 'auto' : 'none',
                        overflow: content.length > 0 ? 'auto' : 'hidden',
                        height: expanded ? '62px' : '42px'
                    }}
                />
            </TextAreaContainer>
            {expanded && (
                <Footer>
                    <Save>
                        {!hideCancel && (
                            <CancelButton onClick={onClose} disabled={creating.busy} tabIndex={-1}>
                                Cancel
                            </CancelButton>
                        )}
                        <PostButton onClick={handleSubmit} disabled={creating.busy}>
                            {actionLabel}
                        </PostButton>
                    </Save>
                </Footer>
            )}
        </div>
    );
})`
  margin-top: 12px;
  margin-bottom: ${({hideCancel}) => hideCancel ? "4px" : "12px"};
  border: 1px solid ${({theme}) =>
          theme.color.name === 'dark' ? themecolors.dark_background_7 : themecolors.light_border};
  border-radius: 24px;
  background-color: transparent;
  overflow: hidden;

  textarea {
    border: none;
    padding: 12px 18px;
    font-size: 14px;
    line-height: 19px;
    width: 100%;
    height: ${({expanded}) => (expanded ? '62px' : '42px')};
    resize: none;
    background-color: transparent;

    &:focus {
      outline: none;
    }
  }

  textarea::placeholder {
    color: ${({theme}) =>
            theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
  textarea:disabled {
    opacity :1;
  }

  ${Footer} {
    width: 100%;
    padding-top: 8px;
    border-top: ${({unopened}) => (unopened ? '1px solid transparent' : 'none')};
  }

  ${Save} {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 8px;
    margin-right: 8px;
    font-size: 13px;
  }

  ${PostButton} {
    margin-left: 12px;
    padding: 8px 16px;
    border-radius: 48px;
    font-weight: 500;
    ${({theme}) => `
        color: ${theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.pure_white};
        background-color: ${theme.color.name === 'dark' ? theme.dark_border : themecolors.pure_black};
    `}
  }

  ${CancelButton} {
    background-color: transparent;
    border: none;
    padding: 8px 12px;
    color: ${({theme}) =>
            theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_medium_impact_text};
  }

  ${TextAreaContainer} {
    display: flex;
   }

`;

export const CommentEditTextInputBox = ({ comment, unopened, hideCancel, onClose }) => {

    const loading = useFeedback();
    const editComment = useEditComment(loading.channel);
    const commentId = comment?.id || 0;

    const [initialContent, setInitialContent] = useState('');
    useRawContent(loading.channel, comment.id, 'comment', setInitialContent);

    const actions = useActions()
    const handleSubmit = async (content) => {
        const parser = new Parser();
        const renderer = new HtmlRenderer();
        const html = renderer.render(parser.parse(content));
        const action = actions.commentEdited(commentId, html);
        try {
            const response = await editComment(commentId, content, "");
            actions.commentEdited(commentId, response.content);
            onClose();
        } catch {
            action.undo();
        }
    };

    return (
        <TextInputBox
            placeholder="Edit your comment"
            actionLabel="Save"
            unopened={unopened}
            onSubmit={handleSubmit}
            hideCancel={hideCancel}
            initialContent={initialContent}
            onClose={onClose}
        />
    );
};

export const CommentTextInputBox = ({ post, comment, unopened, hideCancel, onClose }) => {

    const createComment = useCreateComment();
    const parentId = post?.id || comment?.parent_id;
    const commentId = comment?.id || 0;
    const community = post?.community || comment?.community;

    const handleSubmit = async (content) => {
        await createComment(community, parentId, commentId, content);
    };

    return (
        <TextInputBox
            placeholder="Add a comment"
            actionLabel="Post"
            unopened={unopened}
            onSubmit={handleSubmit}
            hideCancel={hideCancel}
            onClose={onClose}
        />
    );
};

const PostEditTextInputBox = ({ postId, onClose }) => {

    const actions = useActions();
    const {feedbackCh, error} = useFeedback(true);
    const {post} = useStorePost(postId);
    const editPost = useEditPost(feedbackCh);
    const [initialContent, setInitialContent] = useState('');
    const loadContent = useCallback((content) => {
        setInitialContent(content);
    }, [setInitialContent]);
    useRawContent(feedbackCh, postId, 'post', loadContent);

    const handleSubmit = async (content) => {
        actions.postEdited(postId, renderMarkdown(content));
        await editPost(postId, content, '');
    };

    return (
        <TextInputBox
            initialContent={initialContent}
            placeholder="Edit your post"
            actionLabel="Save"
            onSubmit={handleSubmit}
            onClose={onClose}
        />
    );
};


export default TextInputBox;
